import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { PageShell, PageHero } from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Mail, Phone, MapPin } from "lucide-react";
import { GMOAA } from "@/constants/testIds";

const categories = [
  { v: "general", key: "catGeneral" },
  { v: "membership", key: "catMembership" },
  { v: "gr", key: "catGr" },
  { v: "payment", key: "catPayment" },
  { v: "gapl", key: "catGapl" },
  { v: "grievance", key: "catGrievance" },
];

const Contact = () => {
  const { t, lang, site } = useLang();
  const info = site?.site_info || {};
  const address = (lang === "gu" ? info.address_gu : info.address_en) || t("address");
  const [districts, setDistricts] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", district: "", category: "general", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.districts().then(setDistricts); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error(t("required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.createIssue(form);
      setDone(true);
      toast.success(t("thankYou"));
      setForm({ name: "", email: "", mobile: "", district: "", category: "general", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <Toaster position="top-center" richColors />
      <PageHero title={t("contactTitle")} subtitle={t("contactIntro")} />
      <div className="gmoaa-container grid grid-cols-1 gap-10 py-12 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {done && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-gmoaa-green/30 bg-gmoaa-green-light p-4 text-gmoaa-green">
              <CheckCircle2 size={20} /> <p className="text-sm font-medium">{t("thankYou")}</p>
            </div>
          )}
          <form data-testid={GMOAA.contactForm} onSubmit={submit} className="space-y-4 rounded-2xl border border-gmoaa-border-subtle bg-white p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-sm">{t("formName")} *</Label>
                <Input data-testid={GMOAA.contactName} value={form.name} onChange={set("name")} className="border-gmoaa-border-subtle" />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">{t("formEmail")}</Label>
                <Input data-testid={GMOAA.contactEmail} type="email" value={form.email} onChange={set("email")} className="border-gmoaa-border-subtle" />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">{t("formMobile")}</Label>
                <Input data-testid={GMOAA.contactMobile} value={form.mobile} onChange={set("mobile")} className="border-gmoaa-border-subtle" />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">{t("formDistrict")}</Label>
                <Select value={form.district} onValueChange={set("district")}>
                  <SelectTrigger data-testid="contact-district" className="border-gmoaa-border-subtle"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {districts.map((d) => <SelectItem key={d.id} value={d.name_en}>{d.name_en}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block text-sm">{t("formCategory")}</Label>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger data-testid={GMOAA.contactCategory} className="border-gmoaa-border-subtle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.v} value={c.v}>{t(c.key)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-1.5 block text-sm">{t("formSubject")} *</Label>
              <Input data-testid={GMOAA.contactSubject} value={form.subject} onChange={set("subject")} className="border-gmoaa-border-subtle" />
            </div>

            <div>
              <Label className="mb-1.5 block text-sm">{t("formMessage")} *</Label>
              <Textarea data-testid={GMOAA.contactMessage} rows={5} value={form.message} onChange={set("message")} className="border-gmoaa-border-subtle" />
            </div>

            <Button data-testid={GMOAA.contactSubmit} type="submit" disabled={submitting} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">
              {submitting ? t("submitting") : t("submit")}
            </Button>
          </form>
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-gmoaa-border-subtle bg-gmoaa-green p-6 text-white">
            <h3 className="font-serif text-xl font-semibold">{t("contactUs")}</h3>
            <ul className="mt-4 space-y-3 text-sm text-white/80">
              {info.show_address !== false && <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 shrink-0 text-gmoaa-gold" /> {address}</li>}
              {info.show_email !== false && info.email && <li className="flex items-center gap-2"><Mail size={16} className="text-gmoaa-gold" /> {info.email}</li>}
              {info.show_phone !== false && info.phone && <li className="flex items-center gap-2"><Phone size={16} className="text-gmoaa-gold" /> {info.phone}</li>}
            </ul>
          </div>
        </aside>
      </div>
    </PageShell>
  );
};

export default Contact;
